import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import api from "@/lib/api";
import {
  ArrowRight, BadgeCheck, Building2, ChevronRight, Home as HomeIcon, IndianRupee, Loader2,
  MapPin, MessageCircle, Search, ShieldCheck, TrendingUp, Users,
} from "lucide-react";
import { formatPrice, resolveHeroImage, NoImagePlaceholder } from "@/components/ProjectCard";

// ─── SEO head-injection helper ─────────────────────────────────────
// Mutates document.head directly. Uses a data-attribute so we can safely
// wipe our own tags on unmount without touching static tags from index.html.
const OWN_TAG_MARK = "data-inprop-seo";

function useSeoHead({ title, description, canonical, ogImage, jsonLd }) {
  useEffect(() => {
    const created = [];

    const upsertMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute(OWN_TAG_MARK, "1");
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", value);
    };

    const upsertLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute(OWN_TAG_MARK, "1");
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("href", href);
    };

    const originalTitle = document.title;
    if (title) document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);
    upsertLink("canonical", canonical);

    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.setAttribute(OWN_TAG_MARK, "1");
      jsonLdEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = originalTitle;
      // Wipe tags we created; ignore tags we merely mutated (they'll be
      // overwritten on the next page anyway, and touching them here would
      // create empty content while unmounting).
      created.forEach((el) => el.remove());
      if (jsonLdEl) jsonLdEl.remove();
    };
  }, [title, description, canonical, ogImage, JSON.stringify(jsonLd)]);
}

function absoluteUrl(pathname) {
  // Canonical URLs must always point at the production origin so that
  // preview-domain canonicals never leak into the search index. If we're
  // rendering on preview and the env var is unset, we still fall back to
  // the current origin so the page keeps working.
  const canonicalOrigin =
    process.env.REACT_APP_CANONICAL_ORIGIN
    || "https://inpropportal.com";
  if (!canonicalOrigin) return pathname;
  return `${canonicalOrigin.replace(/\/$/, "")}${pathname}`;
}

function LocalityListingCard({ p }) {
  const hero = resolveHeroImage(p);
  const priceLabel = p.price_lakhs ? formatPrice(p.price_lakhs) : null;
  return (
    <Link
      to={`/projects/${p.project_id}`}
      className="sg-card overflow-hidden flex flex-col hover:-translate-y-0.5 hover:shadow-lg transition-all"
      data-testid={`locality-listing-${p.project_id}`}
    >
      <div className="aspect-[16/10] bg-sg-cream-soft relative overflow-hidden">
        {hero ? <img src={hero} alt={p.project_name} className="w-full h-full object-cover" /> : <NoImagePlaceholder />}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-wider text-sg-mute font-semibold">{p.developer}</div>
        <div className="font-display text-lg text-sg-navy mt-1 leading-tight">{p.project_name}</div>
        <div className="text-xs text-sg-mute mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.locality} · {p.zone}</div>
        <div className="mt-auto pt-3 flex items-baseline justify-between border-t border-sg-line/60 mt-3">
          <div className="font-mono text-sm text-sg-gold-deep">{priceLabel || "Price on request"}</div>
          <span className="text-xs font-semibold text-sg-navy inline-flex items-center gap-1">View <ArrowRight className="w-3 h-3" /></span>
        </div>
      </div>
    </Link>
  );
}

function AgentPill({ a }) {
  const phoneDigits = (a.phone || "").replace(/[^0-9]/g, "");
  const waHref = phoneDigits
    ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(`Hi ${a.first_name}, I saw your profile on INPropPortal for ${a.primary_locality || "Pune"}.`)}`
    : null;
  const initials = (a.name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("") || "A";
  const photoBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const photo = a.picture ? (/^https?:\/\//.test(a.picture) ? a.picture : `${photoBase}${a.picture}`) : null;
  return (
    <div className="flex items-center gap-3 p-3 border border-sg-line rounded-xl bg-white" data-testid={`locality-agent-${a.slug}`}>
      {photo ? (
        <img src={photo} alt={a.name} className="w-11 h-11 rounded-xl object-cover border border-sg-line" onError={(e) => { e.currentTarget.style.display = "none"; if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex"; }} />
      ) : null}
      <div style={{ display: photo ? "none" : "flex" }} className="w-11 h-11 rounded-xl bg-sg-cream-soft border border-sg-line items-center justify-center text-sg-navy font-semibold text-sm">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-sg-navy truncate">{a.first_name}</div>
        <div className="text-[11px] text-sg-mute inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-sg-gold-deep" /> MahaCRITI verified</div>
      </div>
      {waHref && (
        <a href={waHref} target="_blank" rel="noreferrer" className="text-xs font-semibold text-sg-gold-deep hover:text-sg-navy inline-flex items-center gap-1" data-testid={`locality-agent-wa-${a.slug}`}>
          <MessageCircle className="w-3.5 h-3.5" /> Contact
        </a>
      )}
    </div>
  );
}

function CrumbTrail({ pathParts }) {
  return (
    <nav className="text-xs text-white/60 mb-4" aria-label="breadcrumb" data-testid="locality-breadcrumb">
      {pathParts.map((p, i) => (
        <span key={p.href}>
          {i > 0 && <ChevronRight className="w-3 h-3 inline mx-1" />}
          {p.href ? (
            <Link to={p.href} className="hover:text-white">{p.label}</Link>
          ) : (
            <span className="text-white/90">{p.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export default function LocalityLanding() {
  const { locality, bhk, status } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    const params = {};
    if (bhk) params.bhk = bhk;
    if (status) params.status = status;
    api.get(`/locality-landing/${locality}`, { params })
      .then(({ data }) => setData(data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [locality, bhk, status]);

  // Deep-link into /buy with the same facet — this is the "convert to search" CTA
  const buyHref = useMemo(() => {
    if (!data) return "/buy";
    const p = new URLSearchParams();
    p.set("locality", data.locality);
    if (data.bhk) p.set("bhk", data.bhk);
    if (data.status) p.set("construction_status", data.status);
    return `/buy?${p.toString()}`;
  }, [data]);

  const path = useMemo(() => {
    if (!data) return "/";
    let base = `/pune/${data.locality_slug}`;
    if (data.bhk_slug) base += `/${data.bhk_slug}`;
    if (data.status_slug) base += `/${data.status_slug}`;
    return base;
  }, [data]);

  // ── SEO meta injection ───────────────────────────────────────────
  const seoTitle = data ? `${data.hero_h1} · INPropPortal` : "Locality — INPropPortal";
  const seoDesc = data?.meta_description || "Verified property listings across Pune's key corridors on INPropPortal.";
  const canonical = data ? absoluteUrl(path) : undefined;
  const jsonLd = useMemo(() => {
    if (!data) return null;
    const items = (data.listings || []).slice(0, 10).map((p, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "url": absoluteUrl(`/projects/${p.project_id}`),
      "name": p.project_name,
    }));
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": absoluteUrl("/") },
          { "@type": "ListItem", "position": 2, "name": "Pune", "item": absoluteUrl("/buy") },
          { "@type": "ListItem", "position": 3, "name": data.locality, "item": absoluteUrl(`/pune/${data.locality_slug}`) },
          ...(data.bhk ? [{ "@type": "ListItem", "position": 4, "name": data.bhk, "item": absoluteUrl(`/pune/${data.locality_slug}/${data.bhk_slug}`) }] : []),
          ...(data.status ? [{ "@type": "ListItem", "position": data.bhk ? 5 : 4, "name": data.status_label, "item": absoluteUrl(path) }] : []),
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": data.hero_h1,
        "numberOfItems": data.total,
        "itemListElement": items,
      },
      ...(data.editorial
        ? [{
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${data.locality} Market Snapshot — INPropPortal Expert View`,
            "articleBody": data.editorial,
            "about": data.locality,
            "author": { "@type": "Organization", "name": "INPropPortal Pune Desk" },
            "publisher": { "@type": "Organization", "name": "INPropPortal", "url": absoluteUrl("/") },
            "mainEntityOfPage": absoluteUrl(path),
          }]
        : []),
    ];
  }, [data, path]);

  useSeoHead({
    title: seoTitle,
    description: seoDesc,
    canonical,
    ogImage: "https://inpropportal.com/og-default.jpg",
    jsonLd,
  });

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-[70vh] flex items-center justify-center text-sg-mute" data-testid="locality-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading {locality}…
        </div>
      </PublicLayout>
    );
  }
  if (notFound || !data) {
    return (
      <PublicLayout>
        <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="locality-notfound">
          <div className="text-center max-w-md">
            <MapPin className="w-12 h-12 mx-auto text-sg-gold-deep" />
            <h1 className="font-display text-4xl text-sg-navy mt-4">We don&apos;t cover that corridor yet.</h1>
            <p className="text-sm text-sg-mute mt-2">
              INPropPortal is live in select Pune corridors today. Explore active localities or head to search.
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              <button onClick={() => navigate("/buy")} className="pill-solid text-sm">Browse all listings</button>
              <button onClick={() => navigate("/")} className="pill-outline text-sm">Home</button>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const crumbs = [
    { label: "Home", href: "/" },
    { label: "Pune", href: "/buy" },
    { label: data.locality, href: data.bhk || data.status ? `/pune/${data.locality_slug}` : null },
    ...(data.bhk ? [{ label: data.bhk, href: data.status ? `/pune/${data.locality_slug}/${data.bhk_slug}` : null }] : []),
    ...(data.status ? [{ label: data.status_label, href: null }] : []),
  ];

  return (
    <PublicLayout>
      {/* HERO ─────────────────────────────────────────────────── */}
      <section className="bg-sg-navy text-white relative overflow-hidden" data-testid="locality-hero">
        <div className="absolute inset-0 bg-cover bg-center opacity-15"
             style={{ backgroundImage: "url(https://images.pexels.com/photos/27826399/pexels-photo-27826399.jpeg)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <CrumbTrail pathParts={crumbs} />
          <div className="overline text-sg-gold mb-3">{data.zone.toUpperCase()} · PUNE</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl" data-testid="locality-h1">
            {data.facet_title !== "Homes" ? (
              <>
                {data.facet_title}{" "}
                <span className="font-serif-italic text-sg-gold">in {data.locality}.</span>
              </>
            ) : (
              <>
                Property in <span className="font-serif-italic text-sg-gold">{data.locality}.</span>
              </>
            )}
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl text-base" data-testid="locality-lede">
            {data.meta_description}
          </p>

          {/* Stat row */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/60">Live projects</div>
              <div className="font-hero text-3xl mt-1">{data.total}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/60">Verified agents</div>
              <div className="font-hero text-3xl mt-1">{data.agents.length}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/60">YoY appreciation</div>
              <div className="font-hero text-3xl mt-1 text-sg-gold">{data.trend?.yoy || "—"}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/60">Corridor</div>
              <div className="font-hero text-xl mt-1">{data.zone}</div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={buyHref} className="pill-solid inline-flex items-center gap-2" data-testid="locality-cta-search">
              <Search className="w-4 h-4" /> Search with these filters
            </Link>
            <Link to={`/agent/${data.agents[0]?.slug || ""}`} className={`pill-outline text-sm inline-flex items-center gap-2 border-white/25 text-white ${data.agents.length ? "" : "pointer-events-none opacity-40"}`}>
              <Users className="w-4 h-4" /> Talk to a {data.locality} agent
            </Link>
          </div>
        </div>
      </section>

      {/* EDITORIAL ─────────────────────────────────────────────── */}
      {data.editorial && (
        <section className="max-w-4xl mx-auto px-6 lg:px-8 py-12" data-testid="locality-editorial">
          <div className="overline text-sg-gold-deep mb-3">EXPERT VIEW · {data.locality.toUpperCase()}</div>
          <article className="font-hero text-2xl md:text-[28px] leading-[1.35] text-sg-navy">
            {data.editorial}
          </article>
          <div className="mt-4 text-xs font-mono text-sg-mute uppercase tracking-wider">
            Written by INPropPortal&apos;s Pune desk · updated for Q1 2026
          </div>
        </section>
      )}

      {/* LISTINGS ────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
        <div className="flex items-baseline justify-between gap-4 mb-6">
          <div>
            <div className="overline text-sg-gold-deep">INVENTORY</div>
            <h2 className="font-display text-3xl text-sg-navy mt-1">
              {data.total > 0
                ? <>{data.total} verified {data.facet_title.toLowerCase()} <span className="font-serif-italic text-sg-gold-deep">in {data.locality}</span></>
                : <>{data.facet_title} in <span className="font-serif-italic text-sg-gold-deep">{data.locality}</span></>}
            </h2>
          </div>
          <Link to={buyHref} className="hidden md:inline-flex text-sm text-sg-navy hover:text-sg-gold-deep items-center gap-1">
            Refine in search <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {data.listings.length === 0 ? (
          <div className="sg-card-cream p-10 text-center max-w-2xl mx-auto" data-testid="locality-listings-empty">
            <Building2 className="w-10 h-10 mx-auto text-sg-gold-deep" />
            <h3 className="font-display text-2xl text-sg-navy mt-3">Nothing matches this exact filter today.</h3>
            <p className="text-sm text-sg-mute mt-2">
              Try a wider search — {data.locality} typically has {data.zone === "Pune West" ? "IT-corridor demand" : "growth-belt inventory"} across all
              configurations. Or WhatsApp a local agent below for off-market options.
            </p>
            <Link to={buyHref} className="pill-solid inline-flex items-center gap-2 mt-5">
              Open search <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="locality-listings-grid">
            {data.listings.map((p) => <LocalityListingCard key={p.project_id} p={p} />)}
          </div>
        )}
      </section>

      {/* AGENTS ─────────────────────────────────────────────── */}
      {data.agents.length > 0 && (
        <section className="bg-white border-y border-sg-line py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="overline text-sg-gold-deep">LOCAL EXPERTS</div>
            <h2 className="font-display text-3xl text-sg-navy mt-1">Agents active in {data.locality}.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-6" data-testid="locality-agents-grid">
              {data.agents.map((a) => <AgentPill key={a.slug} a={a} />)}
            </div>
          </div>
        </section>
      )}

      {/* TREND ─────────────────────────────────────────────── */}
      {data.trend && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="sg-card-cream flex flex-col md:flex-row items-start gap-6 p-6 md:p-8" data-testid="locality-trend">
            <div className="w-14 h-14 rounded-full bg-white border border-sg-line flex items-center justify-center text-sg-gold-deep flex-shrink-0">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="overline text-sg-gold-deep">MARKET SNAPSHOT</div>
              <div className="mt-1 flex items-baseline gap-4 flex-wrap">
                <div className="font-hero text-4xl text-sg-navy">{data.trend.yoy}</div>
                <div className="text-sm text-sg-mute">YoY price movement · {data.locality}</div>
              </div>
              <p className="mt-3 text-sm text-sg-navy/80 leading-relaxed">{data.trend.summary}</p>
            </div>
          </div>
        </section>
      )}

      {/* INTERNAL LINKING — RELATED FACETS (SEO CORE) ──────── */}
      <section className="bg-sg-cream-soft/70 border-t border-sg-line py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="overline text-sg-gold-deep mb-2">RELATED SEARCHES</div>
          <h2 className="font-display text-3xl text-sg-navy">Explore more of {data.locality}.</h2>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="locality-related">
            {/* By BHK */}
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-sg-mute mb-3">By configuration</div>
              <ul className="space-y-1.5">
                {data.related_bhks.map((b) => (
                  <li key={b.slug}>
                    <Link
                      to={`/pune/${data.locality_slug}/${b.slug}${data.status ? "/" + data.status_slug : ""}`}
                      className="text-sm text-sg-navy hover:text-sg-gold-deep inline-flex items-center gap-1"
                      data-testid={`locality-related-bhk-${b.slug}`}
                    >
                      <ChevronRight className="w-3 h-3" /> {b.label} in {data.locality}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* By Status */}
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-sg-mute mb-3">By construction status</div>
              <ul className="space-y-1.5">
                {data.related_statuses.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/pune/${data.locality_slug}${data.bhk_slug ? "/" + data.bhk_slug : ""}/${s.slug}`}
                      className="text-sm text-sg-navy hover:text-sg-gold-deep inline-flex items-center gap-1"
                      data-testid={`locality-related-status-${s.slug}`}
                    >
                      <ChevronRight className="w-3 h-3" /> {s.label} · {data.locality}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Corridor siblings */}
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-sg-mute mb-3">Nearby corridors — {data.zone}</div>
              <ul className="space-y-1.5">
                {data.corridor_siblings.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/pune/${c.slug}${data.bhk_slug ? "/" + data.bhk_slug : ""}${data.status_slug ? "/" + data.status_slug : ""}`}
                      className="text-sm text-sg-navy hover:text-sg-gold-deep inline-flex items-center gap-1"
                      data-testid={`locality-related-corridor-${c.slug}`}
                    >
                      <ChevronRight className="w-3 h-3" /> {c.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 border-t border-sg-line pt-8">
            <div className="text-[11px] font-mono uppercase tracking-wider text-sg-mute mb-3">All Pune corridors</div>
            <div className="flex flex-wrap gap-2">
              {data.vocab.localities.map((l) => (
                <Link key={l.slug} to={`/pune/${l.slug}`} className="chip hover:bg-sg-navy hover:text-white transition-colors" data-testid={`locality-vocab-${l.slug}`}>
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA ─────────────────────────────────────────── */}
      <section className="bg-sg-navy text-white py-14">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="overline text-sg-gold mb-3">READY?</div>
          <h2 className="font-display text-4xl lg:text-5xl">
            {data.total > 0 ? (
              <>See {data.total} live {data.facet_title.toLowerCase()} <span className="font-serif-italic text-sg-gold">in {data.locality}.</span></>
            ) : (
              <>Meet a {data.locality} <span className="font-serif-italic text-sg-gold">agent.</span></>
            )}
          </h2>
          <div className="mt-8 inline-flex gap-3 flex-wrap justify-center">
            <Link to={buyHref} className="pill-solid inline-flex items-center gap-2" data-testid="locality-final-cta-search">
              <Search className="w-4 h-4" /> Open search
            </Link>
            {data.agents[0] && (
              <Link to={`/agent/${data.agents[0].slug}`} className="pill-outline border-white/25 text-white text-sm inline-flex items-center gap-2">
                <BadgeCheck className="w-4 h-4" /> Meet {data.agents[0].first_name}
              </Link>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
